import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useQueryState, parseAsStringLiteral, parseAsInteger } from "nuqs";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Send, Users, BarChart3, Shield, Plus, Eye, Edit, Trash2, Calendar, CheckCircle, XCircle, Clock, FolderPlus, Folder, MoveHorizontal, ChevronLeft, ShieldX, ChevronUp, ChevronDown, Save } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmailScheduler } from "@/components/EmailScheduler";
import { AdvancedEmailScheduler } from "@/components/AdvancedEmailScheduler";
import EmailDragDropEditor from "@/components/EmailDragDropEditor";
import { MergeFieldSelector } from "@/components/MergeFieldSelector";
import { MarketingPageHeader } from "@/components/marketing/MarketingPageHeader";
import { SectionHeader } from "@/components/marketing/SectionHeader";
import { PaginationControls } from "@/components/marketing/PaginationControls";
import { ContactListFilters } from "@/components/marketing/ContactListFilters";
import { ContactListTable } from "@/components/marketing/ContactListTable";
import { BulkActionsBar } from "@/components/marketing/BulkActionsBar";
import { DNCTable } from "@/components/marketing/DNCTable";
import { SaveListDialog } from "@/components/marketing/SaveListDialog";
import { EditListDialog } from "@/components/marketing/EditListDialog";

interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  htmlContent: string | null;
  textContent: string | null;
  componentStructure?: any[]; // Visual editor component structure
  folderId?: number | null;
  isActive: boolean;
  agencyId?: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
}

interface EmailTemplateFolder {
  id: number;
  name: string;
  description?: string | null;
  agencyId?: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
}

interface EmailCampaign {
  id: string;
  name: string;
  templateId: string;
  fromEmail: string;
  fromName: string;
  subject: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
  scheduledAt?: string;
  sentAt?: string;
  recipientCount: number;
  sentCount: number;
  deliveredCount: number;
  openedCount: number;
  clickedCount: number;
  bouncedCount: number;
  createdAt: string;
  updatedAt: string;
}

interface Contact {
  id: string;
  name: string;
  email: string;
  type: 'client' | 'prospect';
  organizationId: number;
}

interface EmailListType {
  id: number;
  name: string;
  description?: string;
  contactIds: string[]; // Array of contact IDs: ["client-1", "prospect-2", ...]
  contactCount: number;
  createdAt: string;
  updatedAt: string;
}

interface DNCEntry {
  id: string;
  email: string;
  reason: string;
  addedAt: string;
  addedBy: string;
}

export default function EmailMarketing() {
  // NUQS: URL-based state management for shareable URLs
  const [activeTab, setActiveTab] = useQueryState(
    "tab",
    parseAsStringLiteral(["templates", "contacts", "analytics", "dnc"]).withDefault("templates")
  );
  const [folderId, setFolderId] = useQueryState("folder", parseAsInteger); // Selected folder ID for navigation
  const [typeFilter, setTypeFilter] = useQueryState(
    "type",
    parseAsStringLiteral(["all", "client", "prospect"]).withDefault("all")
  ); // Contact type filter (all/client/prospect)
  const [searchFilter, setSearchFilter] = useQueryState("search", { defaultValue: "" }); // Search query for contacts
  const [currentPage, setCurrentPage] = useQueryState("page", parseAsInteger.withDefault(1)); // Pagination page for contacts
  
  // Visual editor state
  const [showDragDropEditor, setShowDragDropEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  
  // Template selection and form state
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<EmailCampaign | null>(null);
  
  // Contact list sorting state
  const [sortField, setSortField] = useState<'name' | 'email' | 'type' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Saved lists dialog state
  const [isSaveListDialogOpen, setIsSaveListDialogOpen] = useState(false);
  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [listSearchFilter, setListSearchFilter] = useState('');
  
  // Edit list dialog state
  const [isEditListDialogOpen, setIsEditListDialogOpen] = useState(false);
  const [listToEdit, setListToEdit] = useState<EmailListType | null>(null);
  const [editListName, setEditListName] = useState('');
  const [editListDescription, setEditListDescription] = useState('');
  const [editListSelectedContacts, setEditListSelectedContacts] = useState<Set<string>>(new Set());
  
  // Track if we should auto-select contacts after loading a list (use ref to avoid race conditions)
  // Value is false when not auto-selecting, or a number representing the max count to select
  const shouldAutoSelectContactsRef = useRef<number | false>(false);
  
  // Pagination state for contacts
  const [contactsPerPage] = useState(10);
  
  // Bulk selection state
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  
  // Bulk email state
  const [isBulkEmailDialogOpen, setIsBulkEmailDialogOpen] = useState(false);
  const [bulkEmailMode, setBulkEmailMode] = useState<'template' | 'custom'>('template');
  const [selectedBulkTemplate, setSelectedBulkTemplate] = useState<string>('');
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailContent, setBulkEmailContent] = useState('');
  
  // Folder management state
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<EmailTemplateFolder | null>(null);
  const [folderForm, setFolderForm] = useState({ name: "", description: "" });
  const [selectedTemplateForMove, setSelectedTemplateForMove] = useState<EmailTemplate | null>(null);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [selectedMoveFolder, setSelectedMoveFolder] = useState<EmailTemplateFolder | null>(null);
  
  // Template sorting state
  const [templateSortField, setTemplateSortField] = useState<'name' | 'subject' | 'status' | 'updatedAt' | null>(null);
  const [templateSortDirection, setTemplateSortDirection] = useState<'asc' | 'desc'>('asc');

  // Helper function to strip HTML tags and get plain text preview
  const getTextPreview = (template: EmailTemplate): string => {
    // First try to use textContent if available
    if (template.textContent && template.textContent.trim()) {
      return template.textContent.substring(0, 150) + (template.textContent.length > 150 ? "..." : "");
    }
    
    // Fallback: strip HTML tags from htmlContent
    if (template.htmlContent) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = template.htmlContent;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      return textContent.substring(0, 150) + (textContent.length > 150 ? "..." : "");
    }
    
    return "No content";
  };

  // Template sorting functions
  const handleTemplateSort = (field: 'name' | 'subject' | 'status' | 'updatedAt') => {
    if (templateSortField === field) {
      setTemplateSortDirection(templateSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setTemplateSortField(field);
      setTemplateSortDirection('asc');
    }
  };

  const sortTemplates = (templatesArray: EmailTemplate[]) => {
    if (!templateSortField) return templatesArray;

    return [...templatesArray].sort((a, b) => {
      let aValue: string | boolean | Date;
      let bValue: string | boolean | Date;

      switch (templateSortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'subject':
          aValue = a.subject.toLowerCase();
          bValue = b.subject.toLowerCase();
          break;
        case 'status':
          aValue = a.isActive;
          bValue = b.isActive;
          break;
        case 'updatedAt':
          aValue = new Date(a.updatedAt);
          bValue = new Date(b.updatedAt);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return templateSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return templateSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortableTableHead = ({ 
    field, 
    label, 
    className = "" 
  }: { 
    field: 'name' | 'subject' | 'status' | 'updatedAt'; 
    label: string; 
    className?: string;
  }) => {
    const isSorted = templateSortField === field;
    return (
      <TableHead 
        className={`cursor-pointer select-none hover:bg-muted/50 ${className}`}
        onClick={() => handleTemplateSort(field)}
        data-testid={`sort-${field}`}
      >
        <div className="flex items-center gap-2">
          {label}
          {isSorted ? (
            templateSortDirection === 'asc' ? 
              <ChevronUp className="h-4 w-4" /> : 
              <ChevronDown className="h-4 w-4" />
          ) : (
            <div className="flex flex-col h-4 w-4 opacity-40">
              <ChevronUp className="h-2.5 w-4 -mb-1" />
              <ChevronDown className="h-2.5 w-4" />
            </div>
          )}
        </div>
      </TableHead>
    );
  };

  // Helper function to insert merge field at cursor position
  const insertMergeField = (tag: string, ref: React.RefObject<HTMLInputElement | HTMLTextAreaElement>, stateUpdater: (value: string) => void) => {
    const element = ref.current;
    if (!element) return;

    const start = element.selectionStart || 0;
    const end = element.selectionEnd || 0;
    const currentValue = element.value;
    
    const newValue = currentValue.substring(0, start) + tag + currentValue.substring(end);
    
    // Update the state
    stateUpdater(newValue);
    
    // Set cursor position after the inserted tag
    setTimeout(() => {
      element.focus();
      element.setSelectionRange(start + tag.length, start + tag.length);
    }, 0);
  };

  // Form states
  const [templateForm, setTemplateForm] = useState({
    name: "",
    subject: "",
    htmlContent: "",
    textContent: "",
    componentStructure: undefined as any[] | undefined
  });

  const [campaignForm, setCampaignForm] = useState({
    name: "",
    templateId: "",
    fromEmail: "info@administr.ai",
    fromName: "Administr.ai Insurance",
    subject: "",
    scheduledAt: ""
  });


  const [dncSearch, setDNCSearch] = useState("");
  const [dncEmail, setDNCEmail] = useState("");
  const [dncReason, setDNCReason] = useState("");

  // Dialog states
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isCampaignDialogOpen, setIsCampaignDialogOpen] = useState(false);
  const [isDNCDialogOpen, setIsDNCDialogOpen] = useState(false);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Refs for merge field insertion
  const subjectRef = useRef<HTMLInputElement>(null);
  const htmlContentRef = useRef<HTMLTextAreaElement>(null);
  const textContentRef = useRef<HTMLTextAreaElement>(null);
  const bulkSubjectRef = useRef<HTMLInputElement>(null);
  const bulkContentRef = useRef<HTMLTextAreaElement>(null);

  // Queries
  const { data: templates = [] } = useQuery<EmailTemplate[]>({
    queryKey: ['/api/email/templates'],
  });

  // Campaigns from Campaign Builder (for future implementation)
  const { data: campaigns = [] } = useQuery<EmailCampaign[]>({
    queryKey: ['/api/email/campaigns'],
  });

  // Analytics data from bulk sends (actual email sends)
  const { data: analyticsCampaigns = [] } = useQuery<EmailCampaign[]>({
    queryKey: ['/api/email/analytics/campaigns'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/email/analytics/campaigns');
      return await response.json();
    },
  });

  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ['/api/email/contacts', { type: typeFilter, search: searchFilter || undefined }],
  });

  // Sort contacts (filtering is done by backend)
  const filteredAndSortedContacts = [...contacts]
    .sort((a, b) => {
      if (!sortField) return 0;
      
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle undefined/null values
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      
      if (sortField === 'name' || sortField === 'email') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedContacts.length / contactsPerPage);
  const startIndex = (currentPage - 1) * contactsPerPage;
  const endIndex = startIndex + contactsPerPage;
  const paginatedContacts = filteredAndSortedContacts.slice(startIndex, endIndex);

  // Reset to page 1 when filters change (NUQS handles this automatically, but we ensure it)
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [typeFilter, searchFilter, setCurrentPage, currentPage]);

  // Reset selection when filters change (unless we're auto-selecting)
  useEffect(() => {
    if (shouldAutoSelectContactsRef.current === false) {
      setSelectedContacts(new Set());
    }
  }, [typeFilter, searchFilter, currentPage]);

  // Note: Auto-select is now handled directly in the Load button onClick handler
  // No need for this effect anymore since we're selecting specific contact IDs

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedContacts.size === paginatedContacts.length) {
      setSelectedContacts(new Set());
    } else {
      const newSelection = new Set(paginatedContacts.map(contact => `${contact.type}-${contact.id}`));
      setSelectedContacts(newSelection);
    }
  };

  const toggleSelectContact = (contact: Contact) => {
    const contactKey = `${contact.type}-${contact.id}`;
    const newSelection = new Set(selectedContacts);
    if (newSelection.has(contactKey)) {
      newSelection.delete(contactKey);
    } else {
      newSelection.add(contactKey);
    }
    setSelectedContacts(newSelection);
  };

  const getSelectedContactsData = () => {
    // Get selected contacts from current page only (for display purposes)
    return paginatedContacts.filter(contact => 
      selectedContacts.has(`${contact.type}-${contact.id}`)
    );
  };

  const handleSort = (field: 'name' | 'email' | 'type') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: 'name' | 'email' | 'type') => {
    if (sortField !== field) {
      // Show both arrows in blue color to indicate sortable
      return (
        <div className="flex flex-col w-5 h-5 text-blue-500">
          <ChevronUp className="w-5 h-3" />
          <ChevronDown className="w-5 h-3" />
        </div>
      );
    }
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-5 h-5 text-blue-600" /> : 
      <ChevronDown className="w-5 h-5 text-blue-600" />;
  };

  const { data: folders = [] } = useQuery<EmailTemplateFolder[]>({
    queryKey: ['/api/email/folders'],
  });

  // Helper functions for template organization
  const getValidFolderIds = (): Set<number> => {
    return new Set(folders.map(f => f.id));
  };

  const getUnorganizedTemplates = (): EmailTemplate[] => {
    return templates.filter(t => !t.folderId);
  };

  const getTemplatesInFolder = (folderId: number): EmailTemplate[] => {
    return templates.filter(t => t.folderId === folderId);
  };

  const validateFolderId = (id: number | null | undefined): number | null => {
    if (!id) return null;
    const validFolderIds = getValidFolderIds();
    return validFolderIds.has(id) ? id : null;
  };
  
  // Derive currentFolderView from folderId URL param
  const currentFolderView = folderId 
    ? folders.find(f => f.id === folderId) || null 
    : null;
  
  const setCurrentFolderView = (folder: EmailTemplateFolder | null) => {
    if (folder) {
      setFolderId(folder.id);
    } else {
      setFolderId(null);
    }
  };

  const { data: dncList = [] } = useQuery<DNCEntry[]>({
    queryKey: ['/api/email/dnc', dncSearch],
  });

  const { data: savedLists = [] } = useQuery<EmailListType[]>({
    queryKey: ['/api/email/lists'],
  });

  // Mutations
  const createTemplateMutation = useMutation({
    mutationFn: async (template: typeof templateForm) => {
      console.log('Creating template with data:', template);
      const response = await apiRequest('POST', '/api/email/templates', template);
      const result = await response.json();
      console.log('Template created successfully:', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/templates'] });
      setIsTemplateDialogOpen(false);
      setTemplateForm({ name: "", subject: "", htmlContent: "", textContent: "", componentStructure: undefined });
      // Close visual editor if open
      setShowDragDropEditor(false);
      setEditingTemplate(null);
      toast({ title: "Template created successfully" });
    },
    onError: (error) => {
      console.error('Error creating template:', error);
      toast({ 
        title: "Error creating template", 
        description: error.message || "Unknown error occurred",
        variant: "destructive" 
      });
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: async ({ id, ...template }: { id: number } & typeof templateForm) => {
      const response = await apiRequest('PUT', `/api/email/templates/${id}`, template);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/templates'] });
      setIsTemplateDialogOpen(false);
      setSelectedTemplate(null);
      setTemplateForm({ name: "", subject: "", htmlContent: "", textContent: "", componentStructure: undefined });
      // Close visual editor if open
      setShowDragDropEditor(false);
      setEditingTemplate(null);
      toast({ title: "Template updated successfully" });
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest('DELETE', `/api/email/templates/${id}`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/templates'] });
      setTemplateToDelete(null);
      toast({ title: "Template deleted successfully" });
    },
  });

  const createCampaignMutation = useMutation({
    mutationFn: async (campaign: any) => {
      const response = await apiRequest('POST', '/api/email/campaigns', campaign);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/campaigns'] });
      setIsCampaignDialogOpen(false);
      setCampaignForm({
        name: "",
        templateId: "",
        fromEmail: "info@administr.ai",
        fromName: "Administr.ai Insurance",
        subject: "",
        scheduledAt: ""
      });
      setSelectedContacts(new Set());
      toast({ title: "Campaign created successfully" });
    },
  });

  // Folder mutations
  const createFolderMutation = useMutation({
    mutationFn: async (folder: { name: string; description?: string }) => {
      const response = await apiRequest('POST', '/api/email/folders', folder);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/folders'] });
      setIsFolderDialogOpen(false);
      setFolderForm({ name: "", description: "" });
      toast({ title: "Folder created successfully" });
    },
  });

  const updateFolderMutation = useMutation({
    mutationFn: async ({ id, ...folder }: { id: number; name: string; description?: string }) => {
      const response = await apiRequest('PUT', `/api/email/folders/${id}`, folder);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/folders'] });
      setIsFolderDialogOpen(false);
      setSelectedFolder(null);
      setFolderForm({ name: "", description: "" });
      toast({ title: "Folder updated successfully" });
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest('DELETE', `/api/email/folders/${id}`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/folders'] });
      queryClient.invalidateQueries({ queryKey: ['/api/email/templates'] });
      toast({ title: "Folder deleted successfully" });
    },
  });

  const moveTemplateMutation = useMutation({
    mutationFn: async ({ templateId, folderId }: { templateId: number; folderId: number | null }) => {
      const response = await apiRequest('PUT', `/api/email/templates/${templateId}/folder`, { folderId });
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/templates'] });
      setIsMoveDialogOpen(false);
      setSelectedTemplateForMove(null);
      toast({ title: "Template moved successfully" });
    },
  });

  const sendCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      const response = await apiRequest('POST', `/api/email/campaigns/${campaignId}/send`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/campaigns'] });
      toast({ title: "Campaign sent successfully" });
    },
  });

  const scheduleCampaignMutation = useMutation({
    mutationFn: async ({ campaignId, scheduledAt }: { campaignId: string; scheduledAt: string }) => {
      const response = await apiRequest('POST', `/api/email/campaigns/${campaignId}/schedule`, { scheduledAt });
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/campaigns'] });
      toast({ title: "Campaign scheduled successfully" });
    },
  });


  const addToDNCMutation = useMutation({
    mutationFn: async (data: { email: string; reason: string }) => {
      const response = await apiRequest('POST', '/api/email/dnc', data);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to add email to DNC list');
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/dnc', dncSearch] });
      queryClient.invalidateQueries({ queryKey: ['/api/email/contacts'] }); // Invalidate contacts to remove DNC entries
      setIsDNCDialogOpen(false);
      setDNCEmail("");
      setDNCReason("");
      toast({ 
        title: "Success", 
        description: "Email added to DNC list",
        variant: "success"
      });
    },
    onError: (error: Error) => {
      toast({ 
        title: "Error", 
        description: error.message || "Failed to add email to DNC list",
        variant: "destructive" 
      });
    },
  });

  // Deletion confirmation state
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<EmailTemplateFolder | null>(null);
  const [listToDelete, setListToDelete] = useState<EmailListType | null>(null);
  const [dncDeleteDialogOpen, setDncDeleteDialogOpen] = useState(false);
  const [dncEntryToDelete, setDncEntryToDelete] = useState<DNCEntry | null>(null);
  const [listDeleteDialogOpen, setListDeleteDialogOpen] = useState(false);

  const removeFromDNCMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('DELETE', `/api/email/dnc/${id}`);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to remove email from DNC list');
      }
      // Handle 204 No Content response
      if (response.status === 204) {
        return null;
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/dnc', dncSearch] });
      queryClient.invalidateQueries({ queryKey: ['/api/email/contacts'] }); // Invalidate contacts to show removed DNC entries
      setDncDeleteDialogOpen(false);
      setDncEntryToDelete(null);
      toast({ 
        title: "Success", 
        description: "Email removed from DNC list",
        variant: "success"
      });
    },
    onError: (error: Error) => {
      toast({ 
        title: "Error", 
        description: error.message || "Failed to remove email from DNC list",
        variant: "destructive" 
      });
    },
  });

  const createListMutation = useMutation({
    mutationFn: async (listData: { name: string; description: string; contactIds: string[]; contactCount: number }) => {
      const response = await apiRequest('POST', '/api/email/lists', listData);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/lists'] });
      setIsSaveListDialogOpen(false);
      setListName('');
      setListDescription('');
      toast({ title: "Smart list saved successfully" });
    },
    onError: (error) => {
      toast({ 
        title: "Error saving list", 
        description: error.message || "Unknown error occurred",
        variant: "destructive" 
      });
    },
  });

  const updateListMutation = useMutation({
    mutationFn: async ({ id, ...listData }: { id: number; name: string; description?: string; contactIds: string[]; contactCount: number }) => {
      const response = await apiRequest('PUT', `/api/email/lists/${id}`, listData);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/lists'] });
      setIsEditListDialogOpen(false);
      setListToEdit(null);
      setEditListName('');
      setEditListDescription('');
      setEditListSelectedContacts(new Set());
      toast({ title: "Smart list updated successfully" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Error updating list", 
        description: error.message || "Unknown error occurred",
        variant: "destructive" 
      });
    },
  });

  const deleteListMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest('DELETE', `/api/email/lists/${id}`);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to delete email list');
      }
      // Handle 204 No Content response
      if (response.status === 204) {
        return null;
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/lists'] });
      setListDeleteDialogOpen(false);
      setListToDelete(null);
      toast({ 
        title: "Success", 
        description: "Smart list deleted successfully",
        variant: "success"
      });
    },
    onError: (error) => {
      toast({ 
        title: "Error", 
        description: error.message || "Failed to delete email list",
        variant: "destructive" 
      });
    },
  });

  // Get user for fromEmail
  const { data: user } = useQuery<any>({ queryKey: ["/api/auth/user"] });

  const bulkSendEmailMutation = useMutation({
    mutationFn: async (data: {
      recipientIds: string[];
      templateId?: number;
      subject?: string;
      htmlContent?: string;
      textContent?: string;
      fromEmail: string;
      fromName?: string;
    }) => {
      const response = await apiRequest('POST', '/api/email/bulk-send', data);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to send bulk emails');
      }
      return await response.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/contacts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/email/campaigns'] }); // Campaign Builder campaigns
      queryClient.invalidateQueries({ queryKey: ['/api/email/analytics/campaigns'] }); // Bulk send analytics
      setIsBulkEmailDialogOpen(false);
      setSelectedContacts(new Set());
      setBulkEmailSubject('');
      setBulkEmailContent('');
      setSelectedBulkTemplate('');
      
      const total = result.sentCount + result.failedCount + result.skippedCount;
      toast({ 
        title: "Bulk Email Sent", 
        description: `${result.sentCount} sent, ${result.failedCount} failed, ${result.skippedCount} skipped`,
        variant: result.failedCount === 0 ? "success" : "default"
      });
    },
    onError: (error: Error) => {
      toast({ 
        title: "Error", 
        description: error.message || "Failed to send bulk emails",
        variant: "destructive" 
      });
    },
  });

  const handleSaveCurrentList = () => {
    if (!listName.trim()) {
      toast({ 
        title: "Validation Error", 
        description: "List name is required",
        variant: "destructive" 
      });
      return;
    }

    // Get all selected contact IDs across all pages
    const selectedContactIds = Array.from(selectedContacts);
    
    // If no contacts are selected, use all filtered contacts
    const contactIdsToSave = selectedContactIds.length > 0 
      ? selectedContactIds 
      : filteredAndSortedContacts.map(contact => `${contact.type}-${contact.id}`);
    
    const contactCount = contactIdsToSave.length;

    createListMutation.mutate({
      name: listName,
      description: listDescription,
      contactIds: contactIdsToSave,
      contactCount: contactCount
    });
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      htmlContent: template.htmlContent || "",
      textContent: template.textContent || "",
      componentStructure: template.componentStructure
    });
    setIsTemplateDialogOpen(true);
  };

  const handleSaveTemplate = () => {
    console.log('handleSaveTemplate called with templateForm:', templateForm);
    console.log('selectedTemplate:', selectedTemplate);
    
    // Basic validation
    if (!templateForm.name.trim()) {
      toast({ 
        title: "Validation Error", 
        description: "Template name is required",
        variant: "destructive" 
      });
      return;
    }
    
    if (!templateForm.subject.trim()) {
      toast({ 
        title: "Validation Error", 
        description: "Subject line is required",
        variant: "destructive" 
      });
      return;
    }
    
    if (selectedTemplate) {
      console.log('Updating existing template');
      updateTemplateMutation.mutate({
        id: selectedTemplate.id,
        ...templateForm
      });
    } else {
      console.log('Creating new template');
      createTemplateMutation.mutate(templateForm);
    }
  };

  const handleDragDropSaveTemplate = (template: { name: string; subject: string; htmlContent: string; textContent: string; componentStructure?: any[] }) => {
    if (editingTemplate) {
      updateTemplateMutation.mutate({
        id: editingTemplate.id,
        ...template,
        componentStructure: template.componentStructure ?? undefined
      });
    } else {
      createTemplateMutation.mutate({
        ...template,
        componentStructure: template.componentStructure ?? undefined
      });
    }
  };

  const openDragDropEditor = (template?: EmailTemplate) => {
    setEditingTemplate(template || null);
    setShowDragDropEditor(true);
  };

  const handleCreateCampaign = () => {
    const recipients = getSelectedContactsData().map(contact => ({
      email: contact.email,
      name: contact.name,
      organizationId: contact.organizationId.toString(),
      substitutions: {
        firstName: contact.name.split(' ')[0],
        companyName: contact.name
      }
    }));

    createCampaignMutation.mutate({
      ...campaignForm,
      recipients,
      scheduledAt: campaignForm.scheduledAt || null
    });
  };

  // Folder handler functions
  const handleCreateFolder = () => {
    createFolderMutation.mutate(folderForm);
  };

  const handleUpdateFolder = () => {
    if (selectedFolder) {
      updateFolderMutation.mutate({
        id: selectedFolder.id,
        ...folderForm
      });
    }
  };

  const handleEditFolder = (folder: EmailTemplateFolder) => {
    setSelectedFolder(folder);
    setFolderForm({
      name: folder.name,
      description: folder.description || ""
    });
    setIsFolderDialogOpen(true);
  };

  const handleDeleteFolder = (folder: EmailTemplateFolder) => {
    setFolderToDelete(folder);
  };

  const handleDeleteTemplate = (template: EmailTemplate) => {
    setTemplateToDelete(template);
  };

  const handleMoveTemplate = (template: EmailTemplate) => {
    setSelectedTemplateForMove(template);
    setSelectedMoveFolder(null);
    setIsMoveDialogOpen(true);
  };

  const handleConfirmMoveTemplate = (folderId: number | null) => {
    if (selectedTemplateForMove) {
      moveTemplateMutation.mutate({
        templateId: selectedTemplateForMove.id,
        folderId: folderId !== null ? folderId : null
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { color: "secondary", icon: Edit },
      scheduled: { color: "warning", icon: Calendar },
      sending: { color: "info", icon: Clock },
      sent: { color: "success", icon: CheckCircle },
      failed: { color: "destructive", icon: XCircle }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.color as any} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const calculateMetrics = (campaign: EmailCampaign) => {
    const openRate = campaign.sentCount > 0 ? (campaign.openedCount / campaign.sentCount * 100).toFixed(1) : '0';
    const clickRate = campaign.sentCount > 0 ? (campaign.clickedCount / campaign.sentCount * 100).toFixed(1) : '0';
    const bounceRate = campaign.sentCount > 0 ? (campaign.bouncedCount / campaign.sentCount * 100).toFixed(1) : '0';
    
    return { openRate, clickRate, bounceRate };
  };

  if (showDragDropEditor) {
    return (
      <EmailDragDropEditor
        initialTemplate={editingTemplate ? {
          name: editingTemplate.name,
          subject: editingTemplate.subject,
          htmlContent: editingTemplate.htmlContent || "",
          textContent: editingTemplate.textContent || "",
          componentStructure: editingTemplate.componentStructure
        } : undefined}
        onSave={handleDragDropSaveTemplate}
        onCancel={() => {
          setShowDragDropEditor(false);
          setEditingTemplate(null);
        }}
      />
    );
  }

  return (
    <div className="flex-1 space-y-6">
      <MarketingPageHeader
        icon={Mail}
        iconColor="bg-blue-500"
        title="Email Marketing"
        description="Create campaigns, manage templates, and track performance with DNC compliance"
      />

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 bg-[#eff6fe] dark:bg-gray-800">
          <TabsTrigger value="templates" className="flex items-center gap-2 bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">
            <Edit className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="contacts" className="flex items-center gap-2 bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">
            <Users className="h-4 w-4" />
            Contact Lists
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2 bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="dnc" className="flex items-center gap-2 bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">
            <ShieldX className="h-4 w-4" />
            DNC List
          </TabsTrigger>
        </TabsList>


        <TabsContent value="templates" className="space-y-4">
          <SectionHeader
            title="Email Templates"
            actions={
              <>
              <Dialog open={isFolderDialogOpen} onOpenChange={setIsFolderDialogOpen}>
                <DialogTrigger asChild>
                  <PermissionButton
                    permission="marketing:edit"
                    whenDenied="hide"
                    variant="outline"
                    onClick={() => {
                      setSelectedFolder(null);
                      setFolderForm({ name: "", description: "" });
                    }}
                    className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                  >
                    <FolderPlus className="mr-2 h-4 w-4" />
                    Add Folder
                  </PermissionButton>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {selectedFolder ? 'Edit Folder' : 'Create Folder'}
                    </DialogTitle>
                    <DialogDescription>
                      {selectedFolder ? 'Update folder details' : 'Create a new folder to organize your templates'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="folderName">Folder Name</Label>
                      <Input
                        id="folderName"
                        placeholder="Newsletter Templates"
                        value={folderForm.name}
                        onChange={(e) => setFolderForm(prev => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="folderDescription">Description (Optional)</Label>
                      <Input
                        id="folderDescription"
                        placeholder="Templates for monthly newsletters"
                        value={folderForm.description}
                        onChange={(e) => setFolderForm(prev => ({ ...prev, description: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setIsFolderDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={selectedFolder ? handleUpdateFolder : handleCreateFolder}
                      disabled={!folderForm.name.trim()}
                    >
                      {selectedFolder ? 'Update Folder' : 'Create Folder'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              <PermissionButton
                permission="marketing:edit"
                whenDenied="hide"
                variant="outline"
                onClick={() => openDragDropEditor()}
                className="border-2 border-dashed border-blue-300 hover:border-blue-500 text-blue-600 hover:!text-white hover:!bg-[#1d4c99]"
              >
                <Edit className="mr-2 h-4 w-4" />
                Visual Editor
              </PermissionButton>
              <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
                <DialogTrigger asChild>
                  <PermissionButton
                    permission="marketing:edit"
                    whenDenied="hide"
                    onClick={() => {
                      setSelectedTemplate(null);
                      setTemplateForm({ name: "", subject: "", htmlContent: "", textContent: "", componentStructure: undefined });
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Code Editor
                  </PermissionButton>
                </DialogTrigger>
                <DialogContent className="max-w-4xl">
                  <DialogHeader>
                    <DialogTitle>
                      {selectedTemplate ? 'Edit Template' : 'Create Template'}
                    </DialogTitle>
                    <DialogDescription>
                      {selectedTemplate ? 'Update your email template' : 'Create a new email template'}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="templateName">Template Name</Label>
                        <Input
                          id="templateName"
                          placeholder="Monthly Newsletter"
                          value={templateForm.name}
                          onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label htmlFor="templateSubject">Subject Line</Label>
                          <MergeFieldSelector
                            onSelect={(tag) => insertMergeField(tag, subjectRef, (value) => 
                              setTemplateForm(prev => ({ ...prev, subject: value }))
                            )}
                            buttonText="Add Merge Field"
                            buttonSize="sm"
                          />
                        </div>
                        <Input
                          ref={subjectRef}
                          id="templateSubject"
                          placeholder="Your Monthly Insurance Update"
                          value={templateForm.subject}
                          onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label htmlFor="htmlContent">HTML Content</Label>
                          <MergeFieldSelector
                            onSelect={(tag) => insertMergeField(tag, htmlContentRef, (value) => 
                              setTemplateForm(prev => ({ ...prev, htmlContent: value }))
                            )}
                            buttonText="Add Merge Field"
                            buttonSize="sm"
                          />
                        </div>
                        <Textarea
                          ref={htmlContentRef}
                          id="htmlContent"
                          placeholder="HTML email content with merge fields like {{firstName}}"
                          rows={10}
                          value={templateForm.htmlContent}
                          onChange={(e) => setTemplateForm(prev => ({ ...prev, htmlContent: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label htmlFor="textContent">Plain Text Content</Label>
                          <MergeFieldSelector
                            onSelect={(tag) => insertMergeField(tag, textContentRef, (value) => 
                              setTemplateForm(prev => ({ ...prev, textContent: value }))
                            )}
                            buttonText="Add Merge Field"
                            buttonSize="sm"
                          />
                        </div>
                        <Textarea
                          ref={textContentRef}
                          id="textContent"
                          placeholder="Plain text version of your email"
                          rows={15}
                          value={templateForm.textContent}
                          onChange={(e) => setTemplateForm(prev => ({ ...prev, textContent: e.target.value }))}
                        />
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <strong>Enhanced Merge Fields:</strong>
                        <p className="mt-1">Use the "Add Merge Field" button above to insert personalized data including contact info, company details, employee information, plan details, renewal dates, premiums, and much more.</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    <div></div>
                    <div className="flex justify-end space-x-2">
                      <Button variant="outline" onClick={() => setIsTemplateDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleSaveTemplate} disabled={createTemplateMutation.isPending || updateTemplateMutation.isPending}>
                        {(createTemplateMutation.isPending || updateTemplateMutation.isPending) ? "Saving..." : "Save Template"}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              </>
            }
          />

          <div className="space-y-6">
            {/* Breadcrumb Navigation */}
            {currentFolderView && (
              <div className="flex items-center space-x-2 mb-4">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setCurrentFolderView(null)}
                  className="text-blue-600 hover:bg-blue-50 hover:text-black"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back to All Folders
                </Button>
                <span className="text-muted-foreground">/</span>
                <span className="font-medium">{currentFolderView.name}</span>
              </div>
            )}

            {/* Folder View - Show templates inside selected folder */}
            {currentFolderView ? (
              <div className="space-y-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 py-4">
                    <div className="flex items-center gap-2">
                      <Folder className="h-5 w-5 text-blue-600" />
                      <h4 className="text-lg font-medium">{currentFolderView.name}</h4>
                      <Badge variant="secondary">
                        {getTemplatesInFolder(currentFolderView.id).length} templates
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditFolder(currentFolderView)}
                      >
                        <Edit className="h-4 w-4" />
                        Edit Folder
                      </Button>
                    </div>
                  </CardHeader>
                  {currentFolderView.description && (
                    <p className="text-sm text-muted-foreground mb-4">{currentFolderView.description}</p>
                  )}
                </Card>

                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableTableHead field="name" label="Name" />
                        <SortableTableHead field="subject" label="Subject" />
                        <SortableTableHead field="status" label="Status" />
                        <SortableTableHead field="updatedAt" label="Last Updated" />
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortTemplates(getTemplatesInFolder(currentFolderView.id))
                        .map((template) => (
                        <TableRow key={template.id} className="hover:bg-muted/50">
                          <TableCell className="font-medium">{template.name}</TableCell>
                          <TableCell className="max-w-md truncate">{template.subject}</TableCell>
                          <TableCell>
                            <Badge variant={template.isActive ? "default" : "secondary"}>
                              {template.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell>{new Date(template.updatedAt).toLocaleDateString()}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handleMoveTemplate(template)}
                                className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                                data-testid={`button-move-${template.id}`}
                              >
                                <MoveHorizontal className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => openDragDropEditor(template)}
                                className="text-blue-600 hover:!text-white hover:!bg-[#1d4c99]"
                                data-testid={`button-visual-${template.id}`}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Visual
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handleEditTemplate(template)} 
                                className="hover:!text-white hover:!bg-[#1d4c99]"
                                data-testid={`button-code-${template.id}`}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Code
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handleDeleteTemplate(template)}
                                disabled={deleteTemplateMutation.isPending}
                                className="text-red-600 hover:!text-white hover:!bg-red-600"
                                data-testid={`button-delete-${template.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>

                {getTemplatesInFolder(currentFolderView.id).length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No templates in this folder yet. Click "Visual Editor" or "Code Editor" to create your first template, then move it to this folder.
                  </div>
                )}
              </div>
            ) : (
              /* Main View - Show folders and unorganized templates */
              <div className="space-y-6">
                {/* Folders Section */}
                {folders.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-md font-medium text-muted-foreground">Folders</h4>
                    <div className="grid gap-3">
                      {folders.map((folder) => {
                        const folderTemplateCount = getTemplatesInFolder(folder.id).length;
                        return (
                          <Card key={folder.id} className="cursor-pointer hover:bg-accent/50 transition-colors">
                            <CardHeader 
                              className="flex-row items-center justify-between gap-3 space-y-0 py-4"
                              onClick={() => setCurrentFolderView(folder)}
                            >
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg self-center">
                                  <Folder className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                  <h3 className="font-medium text-base leading-tight mb-0.5">{folder.name}</h3>
                                  <p className="text-sm text-muted-foreground leading-tight">
                                    {folderTemplateCount} template{folderTemplateCount !== 1 ? 's' : ''}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditFolder(folder);
                                  }}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteFolder(folder);
                                  }}
                                  disabled={deleteFolderMutation.isPending}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </CardHeader>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Unorganized Templates */}
                {(() => {
                  const unorganizedTemplates = getUnorganizedTemplates();
                  if (unorganizedTemplates.length === 0 && folders.length > 0) return null;
                  
                  return (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <h4 className="text-md font-medium text-muted-foreground">
                          Email Templates
                        </h4>
                        <Badge variant="outline">{unorganizedTemplates.length}</Badge>
                      </div>
                      <Card>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <SortableTableHead field="name" label="Name" />
                              <SortableTableHead field="subject" label="Subject" />
                              <SortableTableHead field="status" label="Status" />
                              <SortableTableHead field="updatedAt" label="Last Updated" />
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortTemplates(unorganizedTemplates).map((template) => (
                              <TableRow key={template.id} className="hover:bg-muted/50">
                                <TableCell className="font-medium">
                                  {template.name}
                                </TableCell>
                                <TableCell className="max-w-md truncate">{template.subject}</TableCell>
                                <TableCell>
                                  <Badge variant={template.isActive ? "default" : "secondary"}>
                                    {template.isActive ? "Active" : "Inactive"}
                                  </Badge>
                                </TableCell>
                                <TableCell>{new Date(template.updatedAt).toLocaleDateString()}</TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end space-x-2">
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={() => handleMoveTemplate(template)}
                                      className="text-gray-600 hover:!text-white hover:!bg-[#1d4c99]"
                                      data-testid={`button-move-${template.id}`}
                                    >
                                      <MoveHorizontal className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={() => openDragDropEditor(template)}
                                      className="text-blue-600 hover:!text-white hover:!bg-[#1d4c99]"
                                      data-testid={`button-visual-${template.id}`}
                                    >
                                      <Edit className="h-4 w-4 mr-1" />
                                      Visual
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={() => handleEditTemplate(template)} 
                                      className="hover:!text-white hover:!bg-[#1d4c99]"
                                      data-testid={`button-code-${template.id}`}
                                    >
                                      <Edit className="h-4 w-4 mr-1" />
                                      Code
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={() => handleDeleteTemplate(template)}
                                      disabled={deleteTemplateMutation.isPending}
                                      className="text-red-600 hover:!text-white hover:!bg-red-600"
                                      data-testid={`button-delete-${template.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Card>
                    </div>
                  );
                })()}

                {templates.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No templates created yet. Click "Visual Editor" or "Code Editor" to create your first template.
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="contacts" className="space-y-4">
          <SectionHeader
            title="Contact Lists"
            actions={
              <div className="flex items-center space-x-2">
              <Badge variant="outline" className="text-sm">
                {filteredAndSortedContacts.length} of {contacts.length} contacts
              </Badge>
              {totalPages > 1 && (
                <Badge variant="secondary" className="text-sm">
                  Page {currentPage} of {totalPages}
                </Badge>
              )}
              </div>
            }
          />

          {savedLists.length > 0 && (
            <Card className="mb-4">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Smart Lists ({savedLists.length})</CardTitle>
                    <CardDescription className="text-sm">
                      Click to load saved filters
                    </CardDescription>
                  </div>
                  <Input
                    placeholder="Search lists..."
                    value={listSearchFilter}
                    onChange={(e) => setListSearchFilter(e.target.value)}
                    className="w-48"
                  />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {savedLists
                    .filter(list => 
                      listSearchFilter === '' || 
                      list.name.toLowerCase().includes(listSearchFilter.toLowerCase()) ||
                      (list.description && list.description.toLowerCase().includes(listSearchFilter.toLowerCase()))
                    )
                    .map((list) => (
                    <div key={list.id} className="flex items-center justify-between p-2 rounded border-l-4 border-l-blue-500 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{list.name}</span>
                          <Badge variant="secondary" className="text-xs px-1.5 py-0.5 h-5">
                            {list.contactCount}
                          </Badge>
                        </div>
                        {list.description && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{list.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            // Select the specific contacts from the list
                            const contactKeys = new Set(list.contactIds);
                            setSelectedContacts(contactKeys);
                            toast({ 
                              title: `Loaded "${list.name}"`, 
                              description: `${list.contactCount} contact${list.contactCount !== 1 ? 's' : ''} selected`
                            });
                          }}
                          className="h-7 px-2 text-xs"
                        >
                          Load
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setListToEdit(list);
                            setEditListName(list.name);
                            setEditListDescription(list.description || '');
                            setEditListSelectedContacts(new Set(list.contactIds || []));
                            setIsEditListDialogOpen(true);
                          }}
                          className="h-7 px-1 text-xs text-blue-600 hover:bg-blue-50"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setListToDelete(list);
                            setListDeleteDialogOpen(true);
                          }}
                          className="h-7 px-1 text-xs text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Available Contacts</CardTitle>
              <CardDescription>
                Contacts from your clients and prospects database (DNC filtered)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-4">
                <ContactListFilters
                  typeFilter={typeFilter}
                  onTypeFilterChange={setTypeFilter}
                  searchFilter={searchFilter}
                  onSearchFilterChange={setSearchFilter}
                  contactType="email"
                  totalContacts={contacts.length}
                  filteredContacts={filteredAndSortedContacts.length}
                  currentPage={currentPage}
                  totalPages={totalPages}
                />
              </div>

              <BulkActionsBar
                selectedCount={selectedContacts.size}
                selectedContactsPreview={getSelectedContactsData().map(c => c.email).join(", ").substring(0, 50) + (getSelectedContactsData().map(c => c.email).join(", ").length > 50 ? "..." : "")}
                onSend={() => {
                  setBulkEmailMode('template');
                  setSelectedBulkTemplate('');
                  setBulkEmailSubject('');
                  setBulkEmailContent('');
                  setIsBulkEmailDialogOpen(true);
                }}
                onSaveList={() => {
                  setIsSaveListDialogOpen(true);
                }}
                onAddToDNC={async () => {
                  const selectedData = getSelectedContactsData();
                  if (selectedData.length === 0) return;

                  try {
                    const results = await Promise.allSettled(
                      selectedData.map(contact =>
                        apiRequest('POST', '/api/email/dnc', {
                          email: contact.email,
                          reason: "manual"
                        })
                      )
                    );

                    const successful = results.filter(r => r.status === 'fulfilled').length;
                    const failed = results.length - successful;

                    queryClient.invalidateQueries({ queryKey: ['/api/email/dnc', dncSearch] });
                    queryClient.invalidateQueries({ queryKey: ['/api/email/contacts'] });
                    setSelectedContacts(new Set());

                    if (failed === 0) {
                      toast({
                        title: "Success",
                        description: `${successful} contact${successful !== 1 ? 's' : ''} added to DNC list`,
                        variant: "success"
                      });
                    } else {
                      toast({
                        title: "Partial Success",
                        description: `${successful} added, ${failed} failed`,
                        variant: "default"
                      });
                    }
                  } catch (error: any) {
                    toast({
                      title: "Error",
                      description: error.message || "Failed to add contacts to DNC list",
                      variant: "destructive"
                    });
                  }
                }}
                sendLabel="Send Email"
                contactType="email"
              />

              <SaveListDialog
                isOpen={isSaveListDialogOpen}
                onOpenChange={setIsSaveListDialogOpen}
                listName={listName}
                onListNameChange={setListName}
                listDescription={listDescription}
                onListDescriptionChange={setListDescription}
                contactCount={selectedContacts.size}
                onSave={handleSaveCurrentList}
                onCancel={() => setIsSaveListDialogOpen(false)}
                isLoading={createListMutation.isPending}
                disabled={false}
              />
              {selectedContacts.size > 0 && (
                <div className="flex justify-end mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedContacts(new Set())}
                  >
                    Clear Selection
                  </Button>
                </div>
              )}

              {/* Bulk Email Dialog */}
              <Dialog open={isBulkEmailDialogOpen} onOpenChange={setIsBulkEmailDialogOpen}>
                <DialogContent className="max-w-3xl">
                  <DialogHeader>
                    <DialogTitle>Send Bulk Email</DialogTitle>
                    <DialogDescription>
                      Send an email to {selectedContacts.size} selected contact{selectedContacts.size !== 1 ? 's' : ''}
                    </DialogDescription>
                  </DialogHeader>
                  
                  <Tabs value={bulkEmailMode} onValueChange={(value) => setBulkEmailMode(value as 'template' | 'custom')}>
                    <TabsList className="grid w-full grid-cols-2 bg-[#eff6fe] dark:bg-gray-800">
                      <TabsTrigger value="template" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Use Template</TabsTrigger>
                      <TabsTrigger value="custom" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Write Custom Email</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="template" className="space-y-4">
                      <div>
                        <Label htmlFor="bulkTemplate">Select Template</Label>
                        <Select value={selectedBulkTemplate} onValueChange={setSelectedBulkTemplate}>
                          <SelectTrigger id="bulkTemplate">
                            <SelectValue placeholder="Choose an email template" />
                          </SelectTrigger>
                          <SelectContent>
                            {templates.filter(t => t.isActive).map((template) => (
                              <SelectItem key={template.id} value={String(template.id)}>
                                {template.name} - {template.subject}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {selectedBulkTemplate && (
                        <div className="p-4 bg-muted rounded-md">
                          <h4 className="text-sm font-medium mb-2">Template Preview</h4>
                          <p className="text-sm">
                            <strong>Subject:</strong> {templates.find(t => t.id === parseInt(selectedBulkTemplate))?.subject}
                          </p>
                          <div className="mt-2 text-sm text-muted-foreground max-h-32 overflow-y-auto">
                            {templates.find(t => t.id === parseInt(selectedBulkTemplate))?.textContent || 
                             templates.find(t => t.id === parseInt(selectedBulkTemplate))?.htmlContent?.substring(0, 200) + '...'}
                          </div>
                        </div>
                      )}
                    </TabsContent>
                    
                    <TabsContent value="custom" className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label htmlFor="bulkSubject">Subject</Label>
                          <MergeFieldSelector
                            onSelect={(tag) => insertMergeField(tag, bulkSubjectRef, (value) => 
                              setBulkEmailSubject(value)
                            )}
                            buttonText="Add Merge Field"
                            buttonSize="sm"
                          />
                        </div>
                        <Input
                          ref={bulkSubjectRef}
                          id="bulkSubject"
                          placeholder="Email subject line"
                          value={bulkEmailSubject}
                          onChange={(e) => setBulkEmailSubject(e.target.value)}
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label htmlFor="bulkContent">Message</Label>
                          <MergeFieldSelector
                            onSelect={(tag) => insertMergeField(tag, bulkContentRef, (value) => 
                              setBulkEmailContent(value)
                            )}
                            buttonText="Add Merge Field"
                            buttonSize="sm"
                          />
                        </div>
                        <Textarea
                          ref={bulkContentRef}
                          id="bulkContent"
                          placeholder="Type your email message here..."
                          rows={10}
                          value={bulkEmailContent}
                          onChange={(e) => setBulkEmailContent(e.target.value)}
                        />
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <strong>Merge Fields:</strong> You can use {'{firstName}'}, {'{lastName}'}, {'{companyName}'} to personalize emails
                      </div>
                    </TabsContent>
                  </Tabs>
                  
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setIsBulkEmailDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={() => {
                        if (bulkEmailMode === 'template' && !selectedBulkTemplate) {
                          toast({ 
                            title: "Please select a template", 
                            variant: "destructive" 
                          });
                          return;
                        }
                        if (bulkEmailMode === 'custom' && (!bulkEmailSubject || !bulkEmailContent)) {
                          toast({ 
                            title: "Please fill in subject and message", 
                            variant: "destructive" 
                          });
                          return;
                        }
                        
                        const selectedData = getSelectedContactsData();
                        if (selectedData.length === 0) {
                          toast({ 
                            title: "No contacts selected", 
                            variant: "destructive" 
                          });
                          return;
                        }

                        const recipientIds = selectedData.map(contact => contact.id);
                        const fromEmail = user?.email || "info@administr.ai";
                        const fromName = user?.firstName && user?.lastName 
                          ? `${user.firstName} ${user.lastName}`
                          : user?.firstName || user?.email || "Administr";

                        if (bulkEmailMode === 'template') {
                          bulkSendEmailMutation.mutate({
                            recipientIds,
                            templateId: parseInt(selectedBulkTemplate),
                            fromEmail,
                            fromName,
                          });
                        } else {
                          bulkSendEmailMutation.mutate({
                            recipientIds,
                            subject: bulkEmailSubject,
                            htmlContent: bulkEmailContent,
                            fromEmail,
                            fromName,
                          });
                        }
                      }}
                      disabled={bulkSendEmailMutation.isPending}
                      data-testid="button-send-bulk-confirm"
                    >
                      <Send className="mr-2 h-4 w-4" />
                      {bulkSendEmailMutation.isPending ? "Sending..." : `Send Email${selectedContacts.size > 1 ? 's' : ''}`}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              
              <ContactListTable
                contacts={contacts.map(c => ({ ...c, email: c.email, phone: undefined }))}
                selectedContacts={selectedContacts}
                onToggleSelect={(contact) => toggleSelectContact(contact as Contact)}
                onToggleSelectAll={toggleSelectAll}
                sortField={sortField || null}
                sortDirection={sortDirection}
                onSort={(field) => handleSort(field as 'name' | 'email' | 'type')}
                contactType="email"
                paginatedContacts={paginatedContacts.map(c => ({ ...c, email: c.email, phone: undefined }))}
                allSelected={selectedContacts.size === paginatedContacts.length && paginatedContacts.length > 0}
              />
              
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalItems={filteredAndSortedContacts.length}
                onPageChange={setCurrentPage}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <SectionHeader title="Email Analytics" />

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Campaigns</CardTitle>
                <Mail className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analyticsCampaigns.length}</div>
                <p className="text-xs text-muted-foreground">
                  {analyticsCampaigns.filter(c => c.status === 'sent').length} completed
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Emails Sent</CardTitle>
                <Send className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analyticsCampaigns.reduce((total, campaign) => total + campaign.sentCount, 0)}
                </div>
                <p className="text-xs text-muted-foreground">
                  This month
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Emails Delivered</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analyticsCampaigns.reduce((total, campaign) => total + campaign.deliveredCount, 0)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Successfully delivered
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Open Rate</CardTitle>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analyticsCampaigns.length > 0 
                    ? (analyticsCampaigns.reduce((total, campaign) => {
                        const rate = campaign.sentCount > 0 ? (campaign.openedCount / campaign.sentCount) : 0;
                        return total + rate;
                      }, 0) / analyticsCampaigns.length * 100).toFixed(1)
                    : '0'}%
                </div>
                <p className="text-xs text-muted-foreground">
                  Across all campaigns
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Bounced</CardTitle>
                <XCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analyticsCampaigns.reduce((total, campaign) => total + campaign.bouncedCount, 0)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Failed deliveries
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Unsubscribed</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dncList.filter(entry => entry.reason === 'unsubscribed').length}
                </div>
                <p className="text-xs text-muted-foreground">
                  From campaigns
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Spam Complaints</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {dncList.filter(entry => entry.reason === 'complaint').length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Reported as spam
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">DNC Contacts</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dncList.length}</div>
                <p className="text-xs text-muted-foreground">
                  Protected contacts
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Campaign Analytics Table */}
          <Card>
            <CardHeader>
              <CardTitle>Campaign Performance Details</CardTitle>
              <CardDescription>
                Detailed analytics for each email campaign including delivery and engagement metrics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Emails Sent</TableHead>
                    <TableHead>Delivered</TableHead>
                    <TableHead>Open Rate</TableHead>
                    <TableHead>Click Rate</TableHead>
                    <TableHead>Bounced</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analyticsCampaigns.map((campaign) => {
                    const metrics = calculateMetrics(campaign);
                    return (
                      <TableRow key={campaign.id}>
                        <TableCell className="font-medium">{campaign.name}</TableCell>
                        <TableCell>{campaign.sentCount}</TableCell>
                        <TableCell>{campaign.deliveredCount}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{metrics.openRate}%</span>
                            <div className="w-16 h-2 bg-gray-200 rounded-full">
                              <div 
                                className="h-2 bg-blue-500 rounded-full" 
                                style={{ width: `${Math.min(parseFloat(metrics.openRate), 100)}%` }}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{metrics.clickRate}%</span>
                            <div className="w-16 h-2 bg-gray-200 rounded-full">
                              <div 
                                className="h-2 bg-green-500 rounded-full" 
                                style={{ width: `${Math.min(parseFloat(metrics.clickRate), 100)}%` }}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{campaign.bouncedCount}</span>
                            <Badge variant={campaign.bouncedCount > 0 ? "destructive" : "secondary"} className="text-xs">
                              {metrics.bounceRate}%
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                        <TableCell>
                          {campaign.sentAt 
                            ? new Date(campaign.sentAt).toLocaleDateString()
                            : new Date(campaign.createdAt).toLocaleDateString()
                          }
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dnc" className="space-y-4">
          <SectionHeader
            title="Do Not Contact List"
            actions={
              <Dialog open={isDNCDialogOpen} onOpenChange={setIsDNCDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Add to DNC
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Email to DNC List</DialogTitle>
                  <DialogDescription>
                    Add an email address to the Do Not Contact list
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="dncEmail">Email Address</Label>
                    <Input
                      id="dncEmail"
                      type="email"
                      placeholder="user@example.com"
                      value={dncEmail}
                      onChange={(e) => setDNCEmail(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="dncReason">Reason</Label>
                    <Select value={dncReason} onValueChange={setDNCReason}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                        <SelectItem value="bounced">Email Bounced</SelectItem>
                        <SelectItem value="complaint">Spam Complaint</SelectItem>
                        <SelectItem value="manual">Manual Request</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setIsDNCDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => addToDNCMutation.mutate({ email: dncEmail, reason: dncReason })}
                    disabled={addToDNCMutation.isPending}
                  >
                    {addToDNCMutation.isPending ? "Adding..." : "Add to DNC"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            }
          />

          <DNCTable
            entries={dncList.map(e => ({ ...e, id: e.id }))}
            searchFilter={dncSearch}
            onSearchFilterChange={setDNCSearch}
            onRemove={(entry) => {
              const dncEntry = dncList.find(e => e.id === entry.id || String(e.id) === String(entry.id));
              if (dncEntry) {
                setDncEntryToDelete(dncEntry);
                setDncDeleteDialogOpen(true);
              }
            }}
            contactType="email"
            isLoading={removeFromDNCMutation.isPending}
          />
        </TabsContent>
      </Tabs>

      {/* DNC Delete Confirmation Dialog */}
      <AlertDialog open={dncDeleteDialogOpen} onOpenChange={setDncDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from DNC List?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{dncEntryToDelete?.email}</strong> from the Do Not Contact list? 
              This will allow emails to be sent to this address again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDncEntryToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (dncEntryToDelete) {
                  removeFromDNCMutation.mutate(dncEntryToDelete.id);
                }
              }}
              disabled={removeFromDNCMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeFromDNCMutation.isPending ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditListDialog
        isOpen={isEditListDialogOpen}
        onOpenChange={(open) => {
          setIsEditListDialogOpen(open);
          if (!open) {
            setListToEdit(null);
            setEditListName('');
            setEditListDescription('');
            setEditListSelectedContacts(new Set());
          }
        }}
        list={listToEdit}
        listName={editListName}
        onListNameChange={setEditListName}
        listDescription={editListDescription}
        onListDescriptionChange={setEditListDescription}
        selectedContacts={editListSelectedContacts}
        onSelectedContactsChange={setEditListSelectedContacts}
        availableContacts={filteredAndSortedContacts}
        onSave={(data) => {
          if (listToEdit) {
            updateListMutation.mutate({
              id: listToEdit.id,
              name: data.name,
              description: data.description,
              contactIds: data.contactIds,
              contactCount: data.contactIds.length,
            });
          }
        }}
        onCancel={() => {
          setIsEditListDialogOpen(false);
          setListToEdit(null);
          setEditListName('');
          setEditListDescription('');
          setEditListSelectedContacts(new Set());
        }}
        isLoading={updateListMutation.isPending}
        contactType="email"
      />

      {/* List Deletion Confirmation Dialog */}
      <AlertDialog open={listDeleteDialogOpen} onOpenChange={setListDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Smart List?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{listToDelete?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setListToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (listToDelete) {
                  deleteListMutation.mutate(listToDelete.id);
                }
              }}
              disabled={deleteListMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteListMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Template Deletion Confirmation */}
      <AlertDialog open={!!templateToDelete} onOpenChange={(open) => !open && setTemplateToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Email Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{templateToDelete?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setTemplateToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (templateToDelete) {
                  deleteTemplateMutation.mutate(templateToDelete.id);
                }
              }}
              disabled={deleteTemplateMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteTemplateMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Folder Deletion Confirmation */}
      <AlertDialog open={!!folderToDelete} onOpenChange={(open) => !open && setFolderToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Folder?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{folderToDelete?.name}</strong>? 
              {folderToDelete && getTemplatesInFolder(folderToDelete.id).length > 0 && (
                <> This folder contains {getTemplatesInFolder(folderToDelete.id).length} template(s). 
                They will be moved to unorganized templates.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFolderToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (folderToDelete) {
                  deleteFolderMutation.mutate(folderToDelete.id);
                }
              }}
              disabled={deleteFolderMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteFolderMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Template Dialog */}
      <Dialog open={isMoveDialogOpen} onOpenChange={setIsMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Template</DialogTitle>
            <DialogDescription>
              Select a folder to move "{selectedTemplateForMove?.name}" to
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="moveToFolder">Destination Folder</Label>
              <Select onValueChange={(value) => setSelectedMoveFolder(value === "none" ? null : folders.find(f => f.id === parseInt(value)) || null)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <div className="flex items-center gap-2">
                      <span>No Folder (Unorganized)</span>
                    </div>
                  </SelectItem>
                  {folders.map((folder) => (
                    <SelectItem key={folder.id} value={String(folder.id)}>
                      <div className="flex items-center gap-2">
                        <Folder className="h-4 w-4" />
                        <span>{folder.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsMoveDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleConfirmMoveTemplate(selectedMoveFolder ? selectedMoveFolder.id : null)}
              disabled={moveTemplateMutation.isPending}
            >
              {moveTemplateMutation.isPending ? "Moving..." : "Move to Folder"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}